import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AttendeeOverviewRow, OwnerEvent, clubsBookingApi } from '@/api/clubs-booking';
import {
  AdminDataTable,
  AdminSection,
  adminInputClassName,
  adminPrimaryButtonClassName,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
  adminToolbarActionsClassName,
  adminToolbarClassName,
} from '@/components/admin/AdminLayout';
import AdminShell from '@/components/admin/AdminShell';
import TablePagination from '@/components/common/TablePagination';
import { getDisplayContactEmail, getDisplayContactPhone } from '@/utils/contactDetails';

const AttendeesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<AttendeeOverviewRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId') || '');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRows = async () => {
      setLoading(true);
      try {
        const attendeeData = await clubsBookingApi.listMyAttendeeOverview(selectedEventId || undefined, {
          page,
          limit: pageSize,
        });
        setRows(attendeeData.items || []);
        setRowsTotal(Number(attendeeData.total || 0));
      } finally {
        setLoading(false);
      }
    };
    void loadRows();
  }, [page, pageSize, selectedEventId]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const eventData = await clubsBookingApi.listMyEvents(undefined, { page: 1, limit: 500 });
        setEvents(eventData.items || []);
      } catch {
        setEvents([]);
      }
    };
    void loadEvents();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedEventId]);

  return (
    <AdminShell title="Attendees" subtitle="Parent and child records across your activities">
      <AdminSection>
        <div className={adminToolbarClassName}>
          <h2 className="text-2xl font-semibold">Attendee list</h2>
          <div className={adminToolbarActionsClassName}>
            <select
              className={adminInputClassName}
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">All activities</option>
              {events.map((event) => (
                <option key={event._id} value={event._id}>
                  {event.title}
                </option>
              ))}
            </select>
            <Link
              to="/bookings/new"
              className={adminPrimaryButtonClassName}
            >
              Create booking
            </Link>
          </div>
        </div>

        <AdminDataTable>
          <table className={`${adminTableClassName} min-w-[860px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>First name</th>
                <th className={adminTableHeadCellClassName}>Last name</th>
                <th className={adminTableHeadCellClassName}>Parent</th>
                <th className={adminTableHeadCellClassName}>Contact</th>
                <th className={adminTableHeadCellClassName}>Sessions booked</th>
                <th className={adminTableHeadCellClassName}>Sessions attended</th>
                <th className={adminTableHeadCellClassName}>History</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((attendee) => (
                <tr
                  key={attendee._id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/attendees/${attendee._id}`)}
                >
                  <td className={`${adminTableCellClassName} font-medium`}>{attendee.firstName}</td>
                  <td className={adminTableCellClassName}>{attendee.lastName}</td>
                  <td className={adminTableCellClassName}>{attendee.parentName}</td>
                  <td className={`${adminTableCellClassName} text-slate-600`}>
                    {getDisplayContactEmail(attendee.parentEmail)}
                    {getDisplayContactPhone(attendee.parentEmail, attendee.parentPhone) && (
                      <div className="text-xs">{getDisplayContactPhone(attendee.parentEmail, attendee.parentPhone)}</div>
                    )}
                  </td>
                  <td className={adminTableCellClassName}>{attendee.sessionsBooked}</td>
                  <td className={adminTableCellClassName}>{attendee.sessionsAttended}</td>
                  <td className={adminTableCellClassName}>
                    <button
                      type="button"
                      className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/attendees/${attendee._id}`);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <p className="px-4 py-6 text-sm text-slate-500">Loading attendees...</p>}
          {!loading && rowsTotal === 0 && <p className="px-4 py-6 text-sm text-slate-500">No attendees found.</p>}
          {!loading && rowsTotal > 0 && (
            <TablePagination
              totalItems={rowsTotal}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </AdminDataTable>
      </AdminSection>
    </AdminShell>
  );
};

export default AttendeesPage;

